<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  cartItems: {
    type: Array,
    default: () => [],
  },
  cartValue: {
    type: String,
    default: '',
  },
  contact: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close', 'delete', 'export', 'save']);

const store = useStore();

// Reactive state for editing
const editableCartItems = ref([]);
const editingItemIndex = ref(null);
const editingField = ref(null); // 'name' or 'quantity'
const tempEditValue = ref('');
const isLoading = ref(false);
const hasUnsavedChanges = ref(false);

// Computed properties
const totalItems = computed(() => {
  return editableCartItems.value.reduce((total, item) => total + (item.quantity || 0), 0);
});

const hasItems = computed(() => {
  return editableCartItems.value && editableCartItems.value.length > 0;
});

const originalTotalItems = computed(() => {
  return props.cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
});

// Methods
const closeModal = () => {
  emit('close');
};

const handleEscKey = (event) => {
  if (event.key === 'Escape') {
    if (editingItemIndex.value !== null) {
      cancelEdit();
    } else if (props.show) {
      closeModal();
    }
  }
};

const handleEnterKey = (event) => {
  if (event.key === 'Enter' && editingItemIndex.value !== null) {
    saveEdit();
  }
};

const handleCopyToClipboard = async (text) => {
  if (!text) return;
  
  try {
    await navigator.clipboard.writeText(text);
    useAlert('Carrinho copiado com sucesso');
  } catch (error) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      useAlert('Carrinho copiado com sucesso');
    } catch (err) {
      useAlert('Erro ao copiar o carrinho', 'error');
    }
    document.body.removeChild(textArea);
  }
};

const deleteCart = async () => {
  if (!props.contact?.id) {
    emit('delete');
    closeModal();
    return;
  }
  
  try {
    // Clear both 'carrinho' and 'Carrinho' custom attributes
    await store.dispatch('contacts/deleteCustomAttributes', {
      id: props.contact.id,
      customAttributes: ['carrinho', 'Carrinho'],
    });
    
    useAlert('Carrinho limpo com sucesso');
    emit('delete');
    closeModal();
  } catch (error) {
    useAlert('Erro ao limpar o carrinho', 'error');
  }
};

// Initialize editable items from props
const initializeEditableItems = () => {
  editableCartItems.value = props.cartItems.map((item, index) => ({
    ...item,
    id: index, // Add unique ID for tracking
  }));
  hasUnsavedChanges.value = false;
};

// Add new product
const addNewProduct = async () => {
  const newItem = {
    id: Date.now(), // Temporary unique ID
    name: '',
    quantity: 0
  };
  
  editableCartItems.value.unshift(newItem);
  hasUnsavedChanges.value = true;
  
  // Auto-save after adding new product
  await autoSaveChanges();
  
  // Start editing the new item's name immediately (now at index 0)
  nextTick(() => {
    startEditing(0, 'name');
  });
};

// Start editing an item field
const startEditing = (itemIndex, field) => {
  editingItemIndex.value = itemIndex;
  editingField.value = field;
  tempEditValue.value = String(editableCartItems.value[itemIndex][field]);
  
  nextTick(() => {
    const input = document.querySelector(`input[data-editing="${itemIndex}-${field}"]`);
    if (input) {
      input.focus();
      input.select();
    }
  });
};

// Save edit
const saveEdit = async () => {
  if (editingItemIndex.value === null || editingField.value === null) return;
  
  const itemIndex = editingItemIndex.value;
  const field = editingField.value;
  let value = tempEditValue.value;
  
  // Validation
  if (field === 'name' && !value.trim()) {
    useAlert('Nome do produto não pode estar vazio', 'error');
    return;
  }
  
  if (field === 'quantity') {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue) || numValue < 0) {
      useAlert('Quantidade deve ser um número válido', 'error');
      return;
    }
    value = numValue;
  }
  
  editableCartItems.value[itemIndex][field] = value;
  hasUnsavedChanges.value = true;
  cancelEdit();
  
  // Auto-save after successful edit
  await autoSaveChanges();
};

// Cancel edit
const cancelEdit = () => {
  // If it's a new item with empty name, remove it
  if (editingItemIndex.value !== null && 
      editingField.value === 'name' && 
      !editableCartItems.value[editingItemIndex.value].name.trim()) {
    removeItem(editingItemIndex.value);
  }
  
  editingItemIndex.value = null;
  editingField.value = null;
  tempEditValue.value = '';
};

// Remove item
const removeItem = async (itemIndex) => {
  editableCartItems.value.splice(itemIndex, 1);
  hasUnsavedChanges.value = true;
  
  // If we were editing this item, cancel editing
  if (editingItemIndex.value === itemIndex) {
    cancelEdit();
  }
  
  // Auto-save after removing item
  await autoSaveChanges();
};

// Auto-save changes to backend
const autoSaveChanges = async () => {
  if (!props.contact?.id || !hasUnsavedChanges.value) return;
  
  isLoading.value = true;
  
  try {
    // Convert items back to string format (name,quantity pairs)
    const cartString = editableCartItems.value
      .filter(item => item.name.trim()) // Only include items with names
      .map(item => `${item.name.trim()},${item.quantity}`)
      .join(',');
    
    const customAttributes = props.contact.custom_attributes || {};
    const updatedAttributes = { 
      ...customAttributes, 
      carrinho: cartString || '' // Use empty string if no items
    };
    
    await store.dispatch('contacts/update', {
      id: props.contact.id,
      custom_attributes: updatedAttributes,
    });
    
    hasUnsavedChanges.value = false;
    emit('save');
  } catch (error) {
    useAlert('Erro ao salvar o carrinho', 'error');
  } finally {
    isLoading.value = false;
  }
};

const exportCart = () => {
  if (!hasItems.value) return;
  
  // Create export data
  const exportData = {
    items: editableCartItems.value,
    totalItems: totalItems.value,
    timestamp: new Date().toISOString(),
    rawData: props.cartValue
  };
  
  // Format as CSV
  const csvContent = [
    ['Item', 'Quantidade'].join(','),
    ...editableCartItems.value.map(item => [item.name, item.quantity].join(','))
  ].join('\n');
  
  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `carrinho_${new Date().getTime()}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  useAlert('Carrinho exportado com sucesso');
  emit('export', exportData);
};

// Watchers
watch(() => props.cartItems, () => {
  initializeEditableItems();
}, { immediate: true });

watch(() => props.show, (newShow) => {
  if (newShow) {
    initializeEditableItems();
  } else {
    cancelEdit();
  }
});

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', handleEscKey);
  document.addEventListener('keydown', handleEnterKey);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscKey);
  document.removeEventListener('keydown', handleEnterKey);
});
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1000] flex items-center justify-center p-8 bg-n-slate-12/10 dark:bg-n-slate-1/10 backdrop-blur-sm"
    @click.self="closeModal"
  >
    <div class="w-full max-w-2xl max-h-[90vh] bg-n-solid-2 rounded-xl shadow-xl flex flex-col">
      <!-- Header -->
      <div class="flex items-center justify-between border-b border-n-weak dark:border-n-strong p-5 flex-shrink-0">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-full bg-n-brand/10 flex items-center justify-center">
            <span class="i-lucide-shopping-cart text-n-brand text-lg" />
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-12">
              Carrinho do cliente
            </span>
            <span class="text-sm text-n-slate-11 dark:text-n-slate-11">
              {{ hasItems ? `${totalItems} ${totalItems === 1 ? 'item' : 'itens'} no carrinho` : 'Carrinho vazio' }}
            </span>
          </div>
        </div>
        <!-- Close Button -->
        <button
          class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 dark:text-n-slate-12 hover:enabled:bg-n-alpha-2 dark:hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 dark:focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
          @click="closeModal"
        >
          <span class="i-lucide-x flex-shrink-0" />
        </button>
      </div>

      <!-- Content -->
      <div class="p-6 overflow-y-auto flex-1">
        <!-- Add Product Button -->
        <div class="mb-4">
          <button
            type="button"
            class="w-full inline-flex items-center justify-center gap-2 transition-all duration-200 ease-in-out border border-n-brand rounded-lg outline-1 outline disabled:opacity-50 bg-transparent text-n-brand hover:enabled:bg-n-brand/10 focus-visible:bg-n-brand/10 outline-transparent h-10 px-4 text-sm font-medium"
            :disabled="isLoading"
            @click="addNewProduct"
          >
            <span class="i-lucide-plus" />
            Adicionar Produto
          </button>
        </div>

        <div v-if="hasItems" class="space-y-4">
          <!-- Cart Items -->
          <div class="space-y-3">
            <div class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12 mb-3">
              Itens no carrinho:
            </div>
            
            <div
              v-for="(item, index) in editableCartItems"
              :key="item.id || index"
              class="flex gap-3"
            >
              <!-- Product Name Container (70%) -->
              <div 
                class="flex-[0.7] bg-n-slate-2 dark:bg-n-solid-3 rounded-lg p-4 hover:bg-n-slate-3 dark:hover:brightness-125 transition-colors cursor-pointer"
                @click="startEditing(index, 'name')"
              >
                <div class="text-xs text-n-slate-11 dark:text-n-slate-11 mb-1">
                  Produto:
                </div>
                <!-- Editable Name -->
                <div v-if="editingItemIndex === index && editingField === 'name'" class="flex items-center gap-2" @click.stop>
                  <input
                    v-model="tempEditValue"
                    :data-editing="`${index}-name`"
                    type="text"
                    class="flex-1 px-2 py-1 text-sm !border-none !outline-none focus:!outline-none focus:!ring-0 !bg-n-slate-1 text-n-slate-12 !m-0"
                    placeholder="Nome do produto"
                    @blur="saveEdit"
                    @keyup.enter="saveEdit"
                    @keyup.escape="cancelEdit"
                  />
                  <button
                    type="button"
                    class="px-3 py-1 text-white bg-woot-600 rounded font-semibold flex-shrink-0 hover:bg-woot-500"
                    @click="saveEdit"
                  >
                    <span class="i-lucide-check text-base mt-1" />
                  </button>
                </div>
                <div
                  v-else
                  class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12 p-2 rounded"
                  :class="{ 'text-red-500': !item.name.trim() }"
                >
                  {{ item.name || 'Clique para adicionar nome' }}
                </div>
              </div>

              <!-- Quantity and Actions Container (30%) -->
              <div class="flex-[0.3] bg-n-slate-2 dark:bg-n-solid-3 rounded-lg p-4 hover:bg-n-slate-3 dark:hover:brightness-125 transition-colors flex items-center justify-between cursor-pointer"
                   @click="startEditing(index, 'quantity')">
                <div class="flex flex-col items-start">
                  <div class="text-xs text-n-slate-11 dark:text-n-slate-11 mb-1">
                    Quantidade:
                  </div>
                  
                  <!-- Editable Quantity -->
                  <div v-if="editingItemIndex === index && editingField === 'quantity'" class="flex items-center gap-1" @click.stop>
                    <input
                      v-model="tempEditValue"
                      :data-editing="`${index}-quantity`"
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      class="w-16 px-2 py-1 text-sm !border-none !outline-none focus:!outline-none focus:!ring-0  text-n-slate-12 text-center !m-0"
                      @blur="saveEdit"
                      @keyup.enter="saveEdit"
                      @keyup.escape="cancelEdit"
                    />
                  </div>
                  <div
                    v-else
                    class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12 p-2 rounded"
                  >
                    {{ item.quantity }}
                  </div>
                </div>
                
                <!-- Remove Item Button -->
                <button
                  type="button"
                  class="p-2 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20 rounded transition-colors"
                  @click.stop="removeItem(index)"
                >
                  <span class="i-lucide-trash-2 text-sm" />
                </button>
              </div>
            </div>
          </div>

        </div>

        <!-- Empty State -->
        <div v-else class="text-center py-12">
          <div class="w-16 h-16 mx-auto mb-4 bg-n-slate-3 dark:bg-n-slate-6 rounded-full flex items-center justify-center">
            <span class="i-lucide-shopping-cart text-2xl text-n-slate-8 dark:text-n-slate-4" />
          </div>
          <h3 class="text-lg font-medium text-n-slate-12 dark:text-n-slate-12 mb-2">
            Carrinho vazio
          </h3>
          <p class="text-sm text-n-slate-11 dark:text-n-slate-11 mb-4">
            Clique em "Adicionar Produto" para começar.
          </p>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between border-t border-n-weak dark:border-n-strong p-5 flex-shrink-0">
        <!-- Left Side Buttons -->
        <div class="flex gap-2">
          <button
            v-if="hasItems"
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-red-600 text-white hover:enabled:bg-red-700 focus-visible:bg-red-700 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            :disabled="isLoading"
            @click="deleteCart"
          >
            Limpar Carrinho
          </button>
        </div>

        <!-- Right Side Content -->
        <div class="flex items-center gap-6">
          <!-- Auto-save Status -->
          <div class="text-sm text-n-brand font-medium">
            <span v-if="isLoading" class="flex items-center gap-1">
              <span class="i-lucide-loader-2 animate-spin text-xs" />
              Salvando...
            </span>
            <span v-else>
              Salvo
            </span>
          </div>
          
          <button
            v-if="hasItems"
            type="button"
            class="inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 bg-n-brand text-white hover:enabled:bg-n-brand/90 focus-visible:bg-n-brand/90 outline-transparent h-10 px-4 text-sm font-medium justify-center"
            :disabled="isLoading"
            @click="exportCart"
          >
            Exportar Carrinho
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom scrollbar for the content area */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: transparent;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgba(148, 163, 184, 0.4);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgba(148, 163, 184, 0.6);
}
</style>